import { useCallback, useEffect, useState } from 'react';
import { cmsGraphql, useDeepEqualMemo } from '@xolv-mybrightlink/common';
import { toast } from 'react-toastify';
import { gql } from '@apollo/client';
import { useIntl } from 'react-intl';
import { useFetchBookmarkedEvents } from './useFetchBookmarkedEvents';
import { useEventByCategory, useEventContext } from './useEventContext';
import { CmsEvent, EventResponse } from '../models/EventResult';

const FETCH_EVENTS = gql`
  query Events(
    $day: Int
    $month: Int
    $year: Int
    $pageSize: Int!
    $endCursor: String
  ) {
    events(
      where: {
        startDateQuery: {
          after: { day: $day, month: $month, year: $year }
          inclusive: true
        }
        orderby: { field: EVENT_START_DATE, order: ASC }
      }
      first: $pageSize
      after: $endCursor
    ) {
      nodes {
        databaseId
        title(format: RENDERED)
        slug
        eventsCategories {
          nodes {
            name
            databaseId
            slug
          }
        }
        venue {
          address
          title(format: RENDERED)
        }
        featuredImage {
          node {
            mediaDetails {
              sizes {
                sourceUrl
                name
              }
            }
          }
        }
        startDate
        endDate
        allDay
        cost
      }
      pageInfo {
        endCursor
      }
    }
  }
`;

const FETCH_EVENT_BY_CATEGORY = gql`
  query EventsByCategory(
    $categoryId: ID!
    $day: Int
    $month: Int
    $year: Int
    $pageSize: Int!
    $endCursor: String
  ) {
    eventsCategory(id: $categoryId, idType: DATABASE_ID) {
      id
      databaseId
      name
      slug
      events(
        where: {
          startDateQuery: {
            after: { day: $day, month: $month, year: $year }
            inclusive: true
          }
          orderby: { field: EVENT_START_DATE, order: ASC }
        }
        first: $pageSize
        after: $endCursor
      ) {
        nodes {
          databaseId
          title(format: RENDERED)
          slug
          eventsCategories {
            nodes {
              name
              databaseId
              slug
            }
          }
          venue {
            address
            title(format: RENDERED)
          }
          featuredImage {
            node {
              mediaDetails {
                sizes {
                  sourceUrl
                  name
                }
              }
            }
          }
          startDate
          endDate
          allDay
          cost
        }
        pageInfo {
          endCursor
        }
      }
    }
  }
`;

type EventFetchResult =
  | { type: 'LOADING' }
  | { type: 'ERROR' }
  | { type: 'DATA'; events: CmsEvent[]; nextCursor: string | null };

export const useFetchEvents = (categoryId: string) => {
  const [, dispatch] = useEventContext();
  const { initial, endCursor, loading } = useEventByCategory(categoryId);
  const getBookmarkEvent = useFetchBookmarkedEvents();
  const intl = useIntl();

  return useCallback(
    async (pageSize?: number) => {
      if (loading || (!initial && !endCursor)) {
        return;
      }
      try {
        dispatch({ type: 'event-list-fetching', categoryId });
        const nowDate = new Date();
        const resultEvent = await cmsGraphql.query<EventResponse>({
          query:
            categoryId === 'all-events'
              ? FETCH_EVENTS
              : FETCH_EVENT_BY_CATEGORY,
          variables: {
            pageSize: pageSize ?? 10,
            day: nowDate.getUTCDate(),
            month: nowDate.getUTCMonth() + 1,
            year: nowDate.getUTCFullYear(),
            categoryId,
            endCursor,
          },
        });
        const resultEventWithBookmark = await getBookmarkEvent(
          categoryId === 'all-events'
            ? resultEvent.data.events.nodes
            : resultEvent.data.eventsCategory.events.nodes,
        );
        dispatch({
          type: 'event-list-fetched',
          events: resultEventWithBookmark,
          endCursor:
            categoryId === 'all-events'
              ? resultEvent.data.events.pageInfo.endCursor
              : resultEvent.data.eventsCategory.events.pageInfo.endCursor,
          categoryId,
        });
      } catch (e) {
        toast(intl.formatMessage({ id: 'common.error.message' }), {
          toastId: 'common.error.message',
        });
        dispatch({
          type: 'event-list-error',
          categoryId,
        });
      }
    },
    [categoryId, dispatch, endCursor, getBookmarkEvent, initial, intl, loading],
  );
};

export const useFetchPreviewEvents = (
  categoryId: number | undefined,
  endCursor: string | null,
  pageSize = 10,
  retry?: boolean,
) => {
  const [result, setResult] = useState<EventFetchResult>({ type: 'LOADING' });
  const intl = useIntl();
  const getBookmarkedEvent = useFetchBookmarkedEvents();
  useEffect(() => {
    const fetch = async () => {
      try {
        setResult({ type: 'LOADING' });
        const nowDate = new Date();
        const resultEvent = await cmsGraphql.query<EventResponse>({
          query:
            categoryId === undefined ? FETCH_EVENTS : FETCH_EVENT_BY_CATEGORY,
          variables: {
            pageSize: pageSize,
            day: nowDate.getUTCDate(),
            month: nowDate.getUTCMonth() + 1,
            year: nowDate.getUTCFullYear(),
            categoryId,
            endCursor,
          },
        });
        const resultEventWithBookmark = await getBookmarkedEvent(
          categoryId
            ? resultEvent.data.eventsCategory.events.nodes
            : resultEvent.data.events.nodes,
        );
        setResult({
          type: 'DATA',
          events: resultEventWithBookmark,
          nextCursor: categoryId
            ? resultEvent.data.eventsCategory.events.pageInfo.endCursor
            : resultEvent.data.events.pageInfo.endCursor,
        });
      } catch (e) {
        toast(intl.formatMessage({ id: 'common.error.message' }), {
          toastId: 'common.error.message',
        });
        setResult({ type: 'ERROR' });
      }
    };
    fetch();
  }, [categoryId, endCursor, getBookmarkedEvent, intl, pageSize, retry]);
  return useDeepEqualMemo(result);
};
